import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { colors } from 'src/theme'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { PaymentsSummary } from 'src/interfaces/payment'
import { GSIcon } from 'src/components/shared/GSIcon'
import { formatCurrencyFromCents } from 'src/utils/transformers'

interface Props {
  isOpen?: boolean
  onClose(): void
  onContinue(): void
  paymentsSummary: PaymentsSummary | null
}

const PaymentsSummaryModalComponent = ({ isOpen, onClose, onContinue, paymentsSummary }: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleOpenFundPayments = useCallback(async () => {
    setIsLoading(true)

    try {
      await onContinue()
    } finally {
      setIsLoading(false)
    }
  }, [onClose, onContinue])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      onClose={onClose}
    >
      <div css={tw`bg-white rounded-lg overflow-hidden w-[450px]`}>
        <ModalHeader css={tw`h-[46px]`} title="Fund payments" showCloseButton={false} />
        <div css={tw`p-4`}>
          <GSIcon iconName={'gs_tasks'} css={tw`w-5 h-5 self-center`} />
          <Typography variant="h4" css={tw`mb-1`}>
            Payment summary
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-6`}>
            Here’s a summary of all payments that will be included in your next invoice. If you need
            to make any changes, you can update the payment list before the invoice is finalized.
          </Typography>
          <div css={tw`grid grid-cols-2 gap-4`}>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {paymentsSummary?.contractorPaymentsCount}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Contractor payments
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {paymentsSummary?.contractorFundingAmount &&
                  formatCurrencyFromCents(paymentsSummary.contractorFundingAmount)}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Contractors funding
              </Typography>
            </div>
            {paymentsSummary?.masterContractorPaymentsCount ? (
              <div>
                <Typography css={tw`mb-1`} variant="h2">
                  {paymentsSummary?.masterContractorPaymentsCount}
                </Typography>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Master contractor payments
                </Typography>
              </div>
            ) : null}
            {paymentsSummary?.masterContractorFundingAmount ? (
              <div>
                <Typography css={tw`mb-1`} variant="h2">
                  {formatCurrencyFromCents(paymentsSummary.masterContractorFundingAmount)}
                </Typography>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Master contractors funding
                </Typography>
              </div>
            ) : null}
          </div>
          <WarningContainer
            className="mt-8"
            title="Please note"
            subtitle="All “Requires funding” payments must be processed together. Before creating an invoice, make sure all payments for master contractors and contractors have been uploaded to the appropriate tables. If you miss any payments, you can cancel funding at any time—unless funds have already been sent—and create a new invoice with the full list of payments."
          />
        </div>
        <div
          css={tw`px-4 py-3 flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button
            size="small"
            color="grey"
            className={tw`w-[100px]`}
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </Button>
          <Button
            size="small"
            color="light"
            className={tw`w-[120px]`}
            onClick={handleOpenFundPayments}
            disabled={isLoading}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const PaymentsSummaryModal = memo(PaymentsSummaryModalComponent)
